// _kind-declarations — what a fixture KIND declares, read from its manifest and catalogue entry, never from its name
// (ASK-159 §2.18gb ruling 7). Pure: no THREE, no DOM — headless-tested by scripts/test-kind-declarations.mjs.
//
// Two questions the runtime used to answer from name lists:
//   1. "Does this fixture have a lid whose hinge edge a merchant chooses?" — the Studio read `BOX_KINDS = {longbox,
//      shortbox}`, so a lidded `longbox-tall` (manifest: `interactions: [{kind: "open-close", …}]`) got no hinge control.
//      The answer is the kind's DECLARED open-close interaction — the same declaration the composer wires the lid from
//      (engine/interactions/open-close.js), probe-lid-hinge judges and GCG's `declaredBehaviours` read.
//   2. "Does a newcomer (a row the /instances hydrate lands after boot) go through the composer?" — the newcomer pass
//      sent only kinds carrying a D-062 GRAMMAR socket, so every manifest-slot holder (longbox-tall, shortbox,
//      featured-comic, the tables, the spinner, the shelves) fell to the decor clone path: a body with no lid, no
//      seats and no composed entry. The boot pass composes every kind whose manifest declares what the composer
//      builds, so the newcomer pass now asks the same declaration: a grammar socket, a manifest slot, or a declared
//      interaction. Plain decor (none of the three) keeps the clone path, which is also where luminaire lights live.
//
// AAA precedent: Unreal's Details panel draws a component's properties because the component DECLARES them (UPROPERTY
// reflection), never because the actor's class name is on a list.

/** The interaction kinds a manifest declares, as a Set (empty for a manifest that declares none or is missing). */
export function declaredInteractions(manifest) {
	const list = Array.isArray(manifest?.interactions) ? manifest.interactions : [];
	return new Set(list.map((i) => i?.kind).filter((k) => typeof k === "string" && k));
}

/** Does this manifest declare the named interaction? (`open-close` = a lid with a hinge edge.) */
export function declaresInteraction(manifest, kind) {
	return declaredInteractions(manifest).has(kind);
}

/** Does the composer own this kind — i.e. does it declare something only the composer builds?
 *  `isGrammarSocket` is injected (packages/layout-grammar) so this module stays dependency-free. */
export function composerOwnsKind({ manifest, catalogueEntry, isGrammarSocket }) {
	const grammar = typeof isGrammarSocket === "function" && (catalogueEntry?.sockets || []).some(isGrammarSocket);
	const slots = Array.isArray(manifest?.slots) && manifest.slots.length > 0;
	return !!(grammar || slots || declaredInteractions(manifest).size > 0);
}
